/**
 * Development shortcuts. Career level, unlocks, equipment ammo.
 *
 * ## This is designed to be thrown away
 *
 * It exists so that a feature can be tested without grinding to the level that
 * unlocks it, which is a real need while building and a cheat button once shipped.
 * So it is built to leave cleanly, three ways over:
 *
 *   1. **It is absent unless asked for.** Nothing here runs, and nothing is added to
 *      the DOM, unless the page URL carries `?dev=1` (or `#dev`). A normal visitor
 *      cannot reach it however long they look, so shipping with this file present is
 *      not a mistake — it is just dead weight.
 *   2. **It is one file and one line.** `install(ctx)` from `bootstrapProgression`.
 *      Deleting the file and that call removes every trace; nothing else in the
 *      codebase imports it or knows it exists.
 *   3. **It injects rather than edits.** The pause menu's own markup is untouched —
 *      this appends a row to it at runtime. There is no half-removed state where a
 *      dev control's markup is left behind in a production screen.
 *
 * It is also visibly marked in the UI, in orange, as 開発用 — a shortcut a tester
 * forgets is on is a bug report about balance that wastes an afternoon.
 */

import { EQUIPMENT } from '../game/equipment.data.js';

/** Only ever true when explicitly asked for. */
export function devRequested() {
  if (typeof window === 'undefined') return false;
  try {
    const u = new URL(window.location.href);
    return u.searchParams.get('dev') === '1' || u.hash === '#dev';
  } catch {
    return false;
  }
}

const LEVELS = [1, 6, 14, 22, 30, 40, 55];

export function install(ctx) {
  if (!devRequested()) return null;

  const menu = ctx.hud?.menu;
  const host = menu?.pauseEl;
  if (!host) return null;

  const row = document.createElement('div');
  row.className = 'menu-row dev-row';
  row.innerHTML = `
    <span class="dev-tag">開発用 DEV</span>
    <div class="menu-seg dev-seg">
      ${LEVELS.map((n) => `<button data-devlv="${n}">LV${n}</button>`).join('')}
    </div>`;

  const row2 = document.createElement('div');
  row2.className = 'menu-row dev-row';
  row2.innerHTML = `
    <span class="dev-tag">開発用 DEV</span>
    <div class="menu-seg dev-seg">
      <button data-dev="unlockAll">全解放</button>
      <button data-dev="ammo">装備補充</button>
    </div>`;

  // Before the rule above RESTART, so the dev rows sit at the bottom of the settings
  // block rather than in the middle of things a player actually uses.
  const rule = host.querySelector('.menu-rule[style]');
  if (rule) host.insertBefore(row, rule), host.insertBefore(row2, rule);
  else host.appendChild(row), host.appendChild(row2);

  const sync = () => {
    const lv = ctx.progression?.level ?? 1;
    for (const b of host.querySelectorAll('[data-devlv]')) {
      b.classList.toggle('is-on', Number(b.dataset.devlv) === lv);
    }
  };

  host.addEventListener('click', (e) => {
    const btn = e.target.closest('button');
    if (!btn) return;
    if (btn.dataset.devlv) {
      ctx.progression?.setLevel?.(Number(btn.dataset.devlv));
      // The equipment screen reads the level live, but only repaints when it is
      // open; nudge it so a level change made behind it is not invisible.
      ctx.equipmentScreen?.isOpen && ctx.equipmentScreen._render();
      sync();
      return;
    }
    if (btn.dataset.dev === 'unlockAll') {
      ctx.progression?.setLevel?.(55);
      ctx.attachments?.unlockAll?.();
      // Every weapon to its own max, so mastery parts are reachable too.
      for (const id of Object.keys(ctx.weapons?.defs ?? {})) ctx.attachments?.setLevel?.(id, 40);
      ctx.equipmentScreen?.isOpen && ctx.equipmentScreen._render();
      sync();
      return;
    }
    if (btn.dataset.dev === 'ammo') {
      const eq = ctx.equipment;
      if (eq) {
        eq.count.lethal = 99;
        eq.count.tactical = 99;
        eq._publish();
      }
      const ws = ctx.weapons;
      if (ws?.current) ws.current.reserve = 999;
    }
  });

  sync();

  // A console handle too, for the desktop side where typing is easier than clicking.
  window.__dev = {
    level: (n) => (ctx.progression?.setLevel?.(n), sync(), n),
    unlockAll: () => (ctx.attachments?.unlockAll?.(), ctx.progression?.setLevel?.(55)),
    give: (slot, id) => {
      if (!EQUIPMENT[id]) return `no such equipment: ${id}`;
      ctx.events.emit('loadout:equipment', { [slot]: id });
      return id;
    },
    equipment: () => Object.keys(EQUIPMENT),
  };

  return { row, row2, sync };
}
